import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from 'react-i18next';
import { BsSendFill } from "react-icons/bs";
import axios from "axios";
import { 
  FiPlus, FiX, FiRefreshCw, FiPaperclip, FiMessageCircle, FiBot, FiUser, 
  FiChevronUp, FiChevronDown, FiLeaf, FiTrendingUp, FiShield, FiTarget,
  FiLightbulb, FiBookOpen, FiBarChart3, FiGlobe, FiUsers, FiZap,
  FiFile, FiDownload, FiTrash2
} from "react-icons/fi";
import { 
  FaRobot, FaUser, FaPaperclip, FaSpinner, FaLeaf, FaChartLine, 
  FaShieldAlt, FaBullseye, FaLightbulb, FaBook, FaChartBar, 
  FaGlobe, FaUsers, FaBolt, FaRecycle, FaTree, FaWater, FaWind, FaUpload
} from "react-icons/fa";

const Chatbot = () => {
  const { t } = useTranslation();
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingForFile, setLoadingForFile] = useState(false);
  const [loadingChat, setLoadingChat] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [currentTopic, setCurrentTopic] = useState(null);

  // File modal states
  const [isFileModalOpen, setIsFileModalOpen] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState("");
  const [customDocumentType, setCustomDocumentType] = useState("");

  const inputRef = useRef(null);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);

  const documentTypeKeys = [
    'humanRightsPolicy',
    'socialResponsibilityPolicy',
    'dataPrivacyPolicy',
    'laborCompliancePolicy',
    'safetyPerformanceReport',
    'supplierSustainabilityAssessment',
    'antiCorruptionPolicy',
    'criticalIncidentRiskManagement',
    'ghgEmissionsReport',
    'waterManagementPlan',
    'independentAssuranceStatement',
    'other'
  ];
  
  const documentTypes = documentTypeKeys.map(key => t(`chatbot.documentTypes.${key}`));

  // ESG Quick Actions
  const esgQuickActions = [
    {
      id: 'environmental',
      label: t('chatbot.environmentalImpact'),
      icon: FaTree,
      color: 'green',
      prompts: [
        'How to measure carbon footprint?',
        'What are renewable energy strategies?',
        'How to implement waste reduction?',
        'Water conservation best practices'
      ]
    },
    {
      id: 'social',
      label: t('chatbot.socialResponsibility'),
      icon: FaUsers,
      color: 'blue',
      prompts: [
        'Employee diversity initiatives',
        'Community engagement programs',
        'Health and safety standards',
        'Supply chain ethics'
      ]
    },
    {
      id: 'governance',
      label: t('chatbot.governanceEthics'),
      icon: FaShieldAlt,
      color: 'purple',
      prompts: [
        'Board composition guidelines',
        'Anti-corruption policies',
        'Risk management frameworks',
        'Transparency reporting'
      ]
    },
    {
      id: 'reporting',
      label: t('chatbot.esgReporting'),
      icon: FaChartBar,
      color: 'orange',
      prompts: [
        'GRI standards compliance',
        'TCFD recommendations',
        'SASB framework guidance',
        'ESG metrics tracking'
      ]
    }
  ];

  const handleNewChat = async () => {
    localStorage.removeItem("sessionId");
    localStorage.removeItem("currentQuestionId");
    localStorage.removeItem("currentAnswer");
    setMessages([]);
    setCurrentTopic(null);
  };

  const handleQuickAction = (prompt) => {
    setMessage(prompt);
    setShowQuickActions(false);
    // Auto-send the message
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 100);
  };

  const fetchChatHistory = async (questionId) => {
    setLoadingChat(true);
    try {
      if (questionId) {
        const res = await axios.get(`/chatbot/chat-history?questionId=${questionId}`);
        if (res.data.status && res.data.data) {
          const latestSession = res.data.data;
          localStorage.setItem("sessionId", latestSession.session);
          setMessages(JSON.parse(latestSession.conversation) || []);
        } else {
          // No conversation found for this question, start fresh
          setMessages([]);
          localStorage.removeItem("sessionId");
        }
      } else {
        // No questionId, clear messages
        setMessages([]);
        localStorage.removeItem("sessionId");
      }
      // Clear the current answer when question changes
      localStorage.removeItem("currentAnswer");
    } catch (error) {
      console.error("Error fetching chat history:", error);
      setMessages([]);
      localStorage.removeItem("sessionId");
      localStorage.removeItem("currentAnswer");
    } finally {
      setLoadingChat(false);
    }
  };

  useEffect(() => {
    const questionId = localStorage.getItem("currentQuestionId");
    fetchChatHistory(questionId);
  }, []);

  // Listen for changes in currentQuestionId
  useEffect(() => {
    const handleQuestionChange = (e) => {
      if (e.detail && e.detail.questionId !== undefined) {
        fetchChatHistory(e.detail.questionId);
        // Also refresh files for the new question
        fetchUploadedFiles(e.detail.questionId);
      }
    };

    window.addEventListener("questionChanged", handleQuestionChange);
    return () => window.removeEventListener("questionChanged", handleQuestionChange);
  }, []);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim() || loading) return;

    const userMessage = { role: "user", content: message };
    setMessages((prev) => [...prev, userMessage]);
    setMessage("");
    setLoading(true);

    try {
      const res = await axios.post("/chatbot/chat-completion", {
        questionMetaData: localStorage.getItem("currentQuestion"),
        query: message,
        sessionId: localStorage.getItem("sessionId"),
        questionId: localStorage.getItem("currentQuestionId"),
      });

      if (res.data.status) {
        const assistantMessage = {
          role: "assistant",
          content: res.data.response,
        };
        localStorage.setItem("currentAnswer", res.data.response)
        localStorage.setItem("sessionId", res.data.sessionId);
        setMessages((prev) => [...prev, assistantMessage]);
      }
    } catch (error) {
      console.error("Error sending message:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const toggleFileModal = () => {
    setIsFileModalOpen((prev) => !prev);
    if (!isFileModalOpen) {
      // Reset form when opening modal
      setSelectedDocumentType("");
      setCustomDocumentType("");
      fetchUploadedFiles();
    }
  };

  const fetchUploadedFiles = async (questionId) => {
    try {
      setLoadingForFile(true);
      const targetQuestionId = questionId || localStorage.getItem("currentQuestionId");
      const response = await axios.get(`/chatbot/files?questionId=${targetQuestionId}`);
      setUploadedFiles(response.data.files || []);
    } catch (error) {
      console.error("Error fetching uploaded files:", error);
    } finally {
      setLoadingForFile(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate document type selection
    if (!selectedDocumentType) {
      alert(t('chatbot.pleaseSelectDocumentType'));
      return;
    }

    const otherKey = documentTypeKeys[documentTypeKeys.length - 1]; // 'other' is last
    const otherLabel = t(`chatbot.documentTypes.${otherKey}`);
    const finalDocumentType = selectedDocumentType === otherLabel ? customDocumentType : selectedDocumentType;
    
    if (selectedDocumentType === otherLabel && !customDocumentType.trim()) {
      alert(t('chatbot.pleaseEnterCustomType'));
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("questionId", localStorage.getItem("currentQuestionId"));
    formData.append("documentType", finalDocumentType);

    setUploading(true);
    try {
      const res = await axios.post("/chatbot/upload-file", 
         formData,{headers: { 'Content-Type': 'multipart/form-data'}});
      if (res.data.status) {
        fetchUploadedFiles();
        alert(res.data.message);
        // Reset form
        setSelectedDocumentType("");
        setCustomDocumentType("");
      }
      else {
        if(res.data.limitFlag) {
          alert(res.data.message);
        }
      }
    } catch (err) {
      console.error("Error uploading file:", err);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleFileDelete = async (fileId) => {
    if (!window.confirm(t('chatbot.areYouSureDeleteFile'))) {
      return;
    }

    try {
      const res = await axios.delete(`/chatbot/delete-file/${fileId}`);
      if (res.data.status) {
        setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
        alert(t('chatbot.fileDeletedSuccessfully'));
      }
    } catch (err) {
      console.error("Error deleting file:", err);
      alert(t('chatbot.failedToDeleteFile'));
    }
  };

  const getFileIcon = (fileName) => {
    const extension = fileName?.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return '📄';
      case 'doc':
      case 'docx':
        return '📝';
      case 'xls':
      case 'xlsx':
        return '📊';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return '🖼️';
      default:
        return '📎';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'failed':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'processing':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'completed':
        return 'text-green-600 bg-green-50 border-green-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className={`fixed bottom-4 right-4 bg-white shadow-xl border border-gray-200 flex flex-col z-40 transition-all duration-300 ease-in-out rounded-lg overflow-hidden ${
      isCollapsed ? 'h-20 w-64' : 'h-[500px] w-[380px]'
    }`}>
      {/* Compact Header */}
      <div 
        className="bg-gradient-to-r from-emerald-500 to-green-600 text-white px-4 py-3 flex justify-between items-center cursor-pointer hover:from-emerald-600 hover:to-green-700 transition-colors duration-200"
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <div className="flex items-center space-x-3">
          <div className="w-6 h-6 bg-white/20 rounded-lg flex items-center justify-center">
            <FaRobot className="w-3 h-3" />
          </div>
          <div>
            <h3 className="text-sm font-semibold">{t('chatbot.esgAiAssistant')}</h3>
            <p className="text-xs text-green-100 opacity-80">{t('chatbot.sustainabilityExpert')}</p>
          </div>
        </div>
        <div className="flex items-center space-x-1">
          {!isCollapsed && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowQuickActions(!showQuickActions);
                }}
                className="p-1 bg-white/20 rounded hover:bg-white/30 transition-colors"
                title={t('chatbot.quickActions')}
              >
                <FiZap className="w-3 h-3" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleNewChat();
                }}
                className="p-1 bg-white/20 rounded hover:bg-white/30 transition-colors"
                title={t('chatbot.newChat')}
              >
                <FiPlus className="w-3 h-3" />
              </button>
            </>
          )}
          <div className="p-1 bg-white/20 rounded hover:bg-white/30 transition-colors">
            {isCollapsed ? (
              <FiChevronUp className="w-3 h-3" />
            ) : (
              <FiChevronDown className="w-3 h-3" />
            )}
          </div>
        </div>
      </div>
  
      {/* Quick Actions Panel */}
      {showQuickActions && !isCollapsed && (
        <div className="absolute top-full right-0 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-50 mt-1">
          <div className="p-3">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-gray-800">{t('chatbot.esgQuickActions')}</h4>
              <button
                onClick={() => setShowQuickActions(false)}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <FiX className="w-3 h-3" />
              </button>
            </div>
            <div className="space-y-2">
              {esgQuickActions.map((action) => (
                <div key={action.id} className="border border-gray-200 rounded-md p-2">
                  <div className="flex items-center mb-1">
                    <action.icon className={`w-3 h-3 mr-1 text-${action.color}-500`} />
                    <span className="text-xs font-medium text-gray-700">{action.label}</span>
                  </div>
                  <div className="space-y-0.5">
                    {action.prompts.map((prompt, index) => (
                      <button
                        key={index}
                        onClick={() => handleQuickAction(prompt)}
                        className="block w-full text-left text-xs text-gray-600 hover:text-gray-800 hover:bg-gray-50 px-1 py-0.5 rounded transition-colors"
                      >
                        {prompt}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
  
      {/* Collapsible Content */}
      {!isCollapsed && (
        <>
          {/* Messages Area - Compact */}
          <div
            className="flex-1 p-4 overflow-y-auto bg-gray-50 space-y-3"
            ref={scrollRef}
          >
            {loadingChat ? (
              <div className="flex justify-center items-center h-full">
                <div className="text-center">
                  <FaSpinner className="w-6 h-6 text-green-600 animate-spin mx-auto mb-1" />
                  <p className="text-xs text-gray-600">{t('chatbot.loadingConversation')}</p>
                </div>
              </div>
            ) : messages.length > 0 ? (
              messages
                .filter((msg) => msg.role !== "system")
                .map((msg, index) => (
                  <div
                    key={index}
                    className={`flex items-start space-x-2 ${
                      msg.role === "user" ? "flex-row-reverse space-x-reverse" : ""
                    }`}
                  >
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                      msg.role === "user" 
                        ? "bg-green-600 text-white" 
                        : "bg-emerald-600 text-white"
                    }`}>
                      {msg.role === "user" ? (
                        <FaUser className="w-2.5 h-2.5" />
                      ) : (
                        <FaRobot className="w-2.5 h-2.5" />
                      )}
                    </div>
                    <div className={`max-w-[85%] p-2 rounded-md text-sm ${
                      msg.role === "user"
                        ? "bg-green-600 text-white rounded-br-none"
                        : "bg-white text-gray-800 border border-gray-200 rounded-bl-none"
                    }`}>
                      <p className="leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  </div>
                ))
            ) : (
              <div className="text-center py-8">
                <div className="w-12 h-12 bg-gradient-to-r from-green-100 to-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <FaLeaf className="w-6 h-6 text-green-600" />
                </div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">{t('chatbot.welcomeMessage')}</h4>
                <p className="text-xs text-gray-600 mb-3">{t('chatbot.welcomeDescription')}</p>
                <div className="flex flex-wrap gap-1 justify-center text-xs">
                  <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded">{t('chatbot.environmental')}</span>
                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded">{t('chatbot.social')}</span>
                  <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded">{t('chatbot.governance')}</span>
                </div>
              </div>
            )}
  
            {loading && !loadingChat && (
              <div className="flex items-start space-x-2">
                <div className="w-6 h-6 bg-emerald-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <FaRobot className="w-2.5 h-2.5 text-white" />
                </div>
                <div className="bg-white border border-gray-200 rounded-md p-2">
                  <div className="flex items-center space-x-1">
                    <FaSpinner className="w-3 h-3 text-emerald-600 animate-spin" />
                    <span className="text-xs text-gray-600">AI is thinking...</span>
                  </div>
                </div>
              </div>
            )}
          </div>
  
          {/* Compact Input Area */}
          <div className="border-t bg-white p-4">
            <div className="mb-3">
              <button
                type="button"
                onClick={toggleFileModal}
                className="w-full py-2 px-3 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-200 transition-colors flex items-center justify-center space-x-1"
              >
                <FiPaperclip className="w-3 h-3" />
                <span>{t('chatbot.manageDocuments')}</span>
              </button>
            </div>
  
            <form onSubmit={handleSendMessage} className="flex gap-2">
              <div className="flex-1 relative">
                <textarea
                  ref={inputRef}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={t('chatbot.askAboutEsg')}
                  rows={1}
                  disabled={loadingChat || loading}
                  className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500 text-sm bg-white"
                />
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                  <FaLeaf className="w-3 h-3 text-green-400" />
                </div>
              </div>
              <button
                type="submit"
                disabled={loading || loadingChat || !message.trim()}
                className={`px-3 py-2 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-lg hover:from-emerald-600 hover:to-green-700 focus:outline-none focus:ring-1 focus:ring-green-500 transition-colors flex items-center justify-center ${
                  loading || loadingChat || !message.trim() ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <BsSendFill className="w-3 h-3" />
              </button>
            </form>
            
            {/* Compact Tips */}
            <div className="mt-2 text-xs text-gray-500 text-center">
              💡 {t('chatbot.tryQuickActions')}
            </div>
          </div>
        </>
      )}
  
      {/* Enhanced File Management Modal */}
      {isFileModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-xl shadow-2xl flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-emerald-600 to-green-600 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                    <FiFile className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Document Manager</h2>
                    <p className="text-green-100 text-sm">Upload and manage your ESG documents</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={fetchUploadedFiles}
                    disabled={loadingForFile}
                    className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors disabled:opacity-50"
                    title="Refresh files"
                  >
                    {loadingForFile ? (
                      <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l5-5-5-5v4a10 10 0 00-10 10h4z" />
                      </svg>
                    ) : (
                      <FiRefreshCw className="w-5 h-5 text-white" />
                    )}
                  </button>
                  <button
                    onClick={toggleFileModal}
                    className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                  >
                    <FiX className="w-5 h-5 text-white" />
                  </button>
                </div>
              </div>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Upload Section */}
              <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-lg border border-gray-200 p-5 mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <FiFile className="w-5 h-5 mr-2 text-blue-600" />
                  Upload New Document
                </h3>
                
                {/* Document Type Selection */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Document Type *
                    </label>
                    <select
                      value={selectedDocumentType}
                      onChange={(e) => setSelectedDocumentType(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    >
                      <option value="">Select document type</option>
                      {documentTypes.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  {selectedDocumentType === "Other" && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Custom Document Type *
                      </label>
                      <input
                        type="text"
                        value={customDocumentType}
                        onChange={(e) => setCustomDocumentType(e.target.value)}
                        placeholder="Enter document type..."
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      />
                    </div>
                  )}

                  {/* File Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select File *
                    </label>
                    <input
                      type="file"
                      onChange={handleFileUpload}
                      ref={fileInputRef}
                      className="w-full p-3 border border-gray-300 rounded-lg bg-white file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 transition-colors"
                      disabled={!selectedDocumentType || uploading}
                    />
                  </div>

                  {uploading && (
                    <div className="flex items-center space-x-2 text-blue-600">
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l5-5-5-5v4a10 10 0 00-10 10h4z" />
                      </svg>
                      <span className="text-sm">Uploading document...</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Uploaded Files Section */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <FiDownload className="w-5 h-5 mr-2 text-green-600" />
                  Uploaded Documents ({uploadedFiles.length})
                </h3>

                {uploadedFiles.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                    <FiFile className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-500 text-sm">No documents uploaded yet</p>
                    <p className="text-gray-400 text-xs mt-1">Upload your first document to get started</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {uploadedFiles.map((file, index) => (
                      <div
                        key={file.id || index}
                        className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-3 flex-1 min-w-0">
                            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                              <span className="text-lg">{getFileIcon(file.fileName)}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2 mb-1">
                                <h4 className="font-medium text-gray-800 truncate text-sm">
                                  {file.fileName}
                                </h4>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(file.status)}`}>
                                  {file.status || 'unknown'}
                                </span>
                              </div>
                              
                              <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                                <span>Active: {file.isActive ? "Yes" : "No"}</span>
                              </div>
                              
                              {file.uploadedAt && (
                                <p className="text-xs text-gray-400 mt-1">
                                  Uploaded: {new Date(file.uploadedAt).toLocaleDateString()}
                                </p>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-2 ml-4 flex-shrink-0">
                            {file.id ? (
                              <button
                                onClick={() => handleFileDelete(file.id)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Delete file"
                              >
                                <FiTrash2 className="w-4 h-4" />
                              </button>
                            ) : (
                              <span className="text-gray-400 text-xs" title="Cannot delete - missing file ID">
                                <FiTrash2 className="w-4 h-4" />
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Chatbot;